import { UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { useRBACStore, UserRole } from '../../stores/rbac-store';
import { Org } from '../../types/dynamodb-tables';
import { 
  useUnifiedRBACDistricts,
  useUnifiedRBACSchools,
  useRBACOrganization,
  unifiedOrgKeys
} from '../useUnifiedOrganization';

// Enhanced query keys for RBAC-aware organization queries
export const rbacOrgKeys = {
  all: ['rbac-organizations'] as const,
  districts: (role: UserRole | null) => [...rbacOrgKeys.all, 'districts', { role }] as const,
  schools: (role: UserRole | null, parentOrgId?: string | null) => [...rbacOrgKeys.all, 'schools', { role, parentOrgId }] as const,
  district: (id: string, role: UserRole | null) => [...rbacOrgKeys.all, 'district', id, { role }] as const,
  school: (id: string, role: UserRole | null) => [...rbacOrgKeys.all, 'school', id, { role }] as const,
};

// Hook for fetching districts based on user role (now using unified approach)
export const useRBACDistricts = (
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useUnifiedRBACDistricts(options);
};

// Hook for fetching schools based on user role and selected district (now using unified approach)
export const useRBACSchools = (
  selectedDistrictId: string | undefined,
  options: Omit<UseQueryOptions<Org[], Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useUnifiedRBACSchools(selectedDistrictId, options);
};

// Hook for fetching a specific district with RBAC (now using unified approach)
export const useRBACDistrict = (
  districtId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useRBACOrganization(districtId, options);
};

// Hook for fetching a specific school with RBAC (now using unified approach)
export const useRBACSchool = (
  schoolId: string,
  options: Omit<UseQueryOptions<Org, Error>, 'queryKey' | 'queryFn'> = {}
) => {
  return useRBACOrganization(schoolId, options);
};

// Hook for getting default selections based on user role (optimized with unified approach)
export const useRBACDefaults = () => {
  const { getDefaultDistrictId, getDefaultSchoolId, getUserRole } = useRBACStore();
  const userRole = getUserRole();
  const defaultDistrictId = getDefaultDistrictId();
  const defaultSchoolId = getDefaultSchoolId();
  const queryClient = useQueryClient();

  // Try to get the district from cache first using unified keys
  let defaultDistrict = undefined;
  if (defaultDistrictId && userRole && userRole === 'district_champion') {
    defaultDistrict = queryClient.getQueryData(unifiedOrgKeys.byIdWithRole(defaultDistrictId, userRole));
    if (!defaultDistrict) {
      const districtsList = queryClient.getQueryData(unifiedOrgKeys.rbac.districts(userRole));
      if (Array.isArray(districtsList)) {
        defaultDistrict = districtsList.find((d) => d.orgId === defaultDistrictId);
      }
    }
  }

  // Always call the hook, but control fetching with enabled
  const { data: fetchedDistrict } = useRBACDistrict(defaultDistrictId || '', {
    enabled: !!defaultDistrictId && (userRole as UserRole) !== 'tm_admin' && !defaultDistrict,
  });

  // Fetch default school if available
  const { data: defaultSchool } = useRBACSchool(defaultSchoolId || '', {
    enabled: !!defaultSchoolId && userRole === 'school_champion',
  });

  return {
    defaultDistrict: defaultDistrict || fetchedDistrict,
    defaultSchool,
    defaultDistrictId,
    defaultSchoolId,
    userRole,
  };
};

// Hook for invalidating RBAC queries (optimized with unified keys)
export const useRBACQueryInvalidation = () => {
  const queryClient = useQueryClient();

  const invalidateRBACQueries = () => {
    queryClient.invalidateQueries({ queryKey: rbacOrgKeys.all });
    queryClient.invalidateQueries({ queryKey: unifiedOrgKeys.all });
  };

  const invalidateDistrictQueries = () => {
    // Invalidate for all possible roles to ensure header updates
    queryClient.invalidateQueries({ 
      predicate: (query) => 
        query.queryKey[0] === 'unified-organizations' && 
        query.queryKey[1] === 'rbac' && 
        query.queryKey[2] === 'districts'
    });
  };

  const invalidateSchoolQueries = (parentOrgId?: string) => {
    queryClient.invalidateQueries({ queryKey: unifiedOrgKeys.rbac.schools(null, parentOrgId) });
  };

  return {
    invalidateRBACQueries,
    invalidateDistrictQueries,
    invalidateSchoolQueries
  };
}; 